import {Component, Host, h, State, Listen, Prop, Event, EventEmitter} from '@stencil/core';

@Component({
  tag: 'snap-calendar',
  styleUrl: 'snap-calendar.scss',
  scoped: true,
})
export class SnapCalendar {
  @Prop({ reflect: true }) yearStart: string = '1900'
  @Prop({ reflect: true }) yearEnd: string = '2100'
  @Prop({ reflect: true }) errorDescription: string = ''

  @State() year = null
  @State() month = null
  @State() selectedDay = null
  @State() monthDetails = null
  @State() showDatePicker = false
  @State() isMobile: boolean
  @State() isMobileCalendarShow = true

  @Event({ eventName: 'snap-calendar-selected', bubbles: true, composed: true }) snapCalendarSelected: EventEmitter<number>

  private mobileScreenWidth = 768
  private oneDay = 60 * 60 * 24 * 1000
  private todayTimestamp = Date.now() - (Date.now() % this.oneDay) + (new Date().getTimezoneOffset() * 1000 * 60);
  private DAYS_MAP = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  private MONTH_MAP = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  private inputRef: HTMLSnapInputElement
  private datePickerContainerRef: HTMLDivElement
  private mobilePickerMonthsRef: HTMLSnapMobilePickerElement
  private mobilePickerYearsRef: HTMLSnapMobilePickerElement
  private manualInputFocus = false

  @Listen('click', { target: 'window' })
  checkForClickOutside(e: any) {
    if (this.showDatePicker && (!this.inputRef?.contains(e.target) && !this.datePickerContainerRef?.contains(e.target)) && !this.isMobile) {
      this.showDatePicker = false
    }
  }

  @Listen('resize', { target: 'window' })
  checkScreen() {
    this.isMobile = document.body.clientWidth < this.mobileScreenWidth
  }

  componentWillLoad() {
    this.checkScreen()

    const date = new Date();
    this.year = date.getFullYear();
    this.month = date.getMonth();
    this.monthDetails = this.getMonthDetails(this.year, this.month)
  }

  getNumberOfDays =(year, month)=> {
    return 40 - new Date(year, month, 40).getDate();
  }

  getDayDetails = (args) => {
    const date = args.index - args.firstDay
    const day = args.index % 7

    let prevMonth = args.month - 1
    let prevYear = args.year

    let nextMonth = args.month + 1
    let nextYear = args.year

    if (prevMonth < 0) {
      prevMonth = 11
      prevYear--
    }

    if (nextMonth > 11) {
      nextMonth = 0
      nextYear++
    }

    const prevMonthNumberOfDays = this.getNumberOfDays(prevYear, prevMonth)
    const _date = (date < 0 ? prevMonthNumberOfDays + date : date % args.numberOfDays) + 1
    const month = date < 0 ? -1 : date >= args.numberOfDays ? 1 : 0

    let timestamp = null

    if (month === 0) {
      timestamp = new Date(args.year, args.month, _date).getTime()
    }
    else if (month === - 1) {
      timestamp = new Date(prevYear, prevMonth, _date).getTime()
    }
    else {
      timestamp = new Date(nextYear, nextMonth, _date).getTime()
    }

    return {
      date: _date,
      day,
      month,
      timestamp,
      dayString: this.DAYS_MAP[day]
    }
  }

  getMonthDetails = (year, month) => {
    const firstDay = (new Date(year, month)).getDay()  // Tuesday
    const numberOfDays = this.getNumberOfDays(year, month)
    const monthArray = []
    const rows = 6
    let currentDay = null
    let index = 0
    const cols = 7

    for(let row= 0; row < rows; row++) {
      for(let col= 0; col < cols; col++) {
        currentDay = this.getDayDetails({
          index,
          numberOfDays,
          firstDay,
          year,
          month
        });
        monthArray.push(currentDay)
        index++
      }
    }
    return monthArray
  }

  setMonth = (offset) => {
    let year = this.year;
    let month = this.month + offset;
    if (month === -1) {
      month = 11;
      year--;
    }
    else if (month === 12) {
      month = 0;
      year++;
    }
    this.year = year
    this.month = month
    this.monthDetails = this.getMonthDetails(year, month)
  }

  setToday = () => {
    this.selectedDay = this.todayTimestamp
    this.setDateToInput(this.todayTimestamp)

    const date = new Date();
    this.year = date.getFullYear();
    this.month = date.getMonth();
    this.monthDetails = this.getMonthDetails(date.getFullYear(), date.getMonth())
  }

  setYear = (year) => {
    this.year = year.value
    this.monthDetails = this.getMonthDetails(this.year, this.month)
  }

  getMonth = (month) => {
    return this.MONTH_MAP[Math.max(Math.min(11, month), 0)] || 'Month'
  }

  isCurrentDay = (day) => {
    return day.timestamp === this.todayTimestamp
  }

  isSelectedDay = (day) => {
    return day.timestamp === this.selectedDay
  }

  onDateClick = (day)=> {
    this.selectedDay = day.timestamp
    this.setDateToInput(day.timestamp)

    this.snapCalendarSelected.emit(day.timestamp)
  }

  setDateToInput = (timestamp)=> {
    this.inputRef.value = this.getDateStringFromTimestamp(timestamp)
  }

  getDateStringFromTimestamp = (timestamp) => {
    const dateObject = new Date(timestamp)
    const month = dateObject.getMonth() + 1
    const date = dateObject.getDate()
    return (month < 10 ? '0' + month : month) + '/' + (date < 10 ? '0' + date : date) + '/' + dateObject.getFullYear()
  }

  renderCalendar = () => {
    const days = this.monthDetails.map((day, index) => {
      return (
        <div
          class={`calendar-day-container  ${day.month !== 0 ? 'gray' : ''} ${this.isCurrentDay(day) ? 'current' : ''} ${this.isSelectedDay(day) ? 'selected' : ''}  `} key={index}>
          <div class="calendar-day">
            <span onClick={() => {
              this.onDateClick(day)
            }}>
              {day.date}
            </span>
          </div>
        </div>
      )
    })

    return (
      <div class="calendar-container">
        <div class="calendar-head">
          {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map((d,i)=><div key={i} class='calendar-head-name'>{d}</div>)}
        </div>
        <div class='calendar-body'>
          { days }
        </div>
      </div>
    )
  }

  onInputDateChangeHandler = (e) => {
    this.setMainDate(e.detail)
  }

  onInputBlurHandler = () => {
    if (this.selectedDay) {
      this.setDateToInput(0o0000000000) // reset value state in snap-input
      this.setDateToInput(this.selectedDay)
    }
  }

  onInputFocusHandler = () => {
    if (this.manualInputFocus) {
      this.manualInputFocus = false
      return
    }

    this.showDatePicker = true
  }

  setMainDate = (timestamp: number) => {
    const date = new Date(timestamp)

    this.selectedDay = timestamp
    this.year = date.getFullYear()
    this.month = date.getMonth()
    this.monthDetails = this.getMonthDetails(this.year, this.month)

    this.snapCalendarSelected.emit(timestamp)
  }

  enterManually = () => {
    this.showDatePicker = false
    this.isMobileCalendarShow = true

    this.manualInputFocus = true

    const input = this.inputRef.querySelector('input')
    const inputEnd = input.value.length
    input.setSelectionRange(inputEnd, inputEnd)
    input.focus()
  }

  showMobileDatePicker = () => {
    this.isMobileCalendarShow = !this.isMobileCalendarShow

    if (!this.isMobileCalendarShow) {
      setTimeout(() => {
        this.mobilePickerMonthsRef.options = [
          {value: '0', text: 'January'},
          {value: '1', text: 'February'},
          {value: '2', text: 'March'},
          {value: '3', text: 'April'},
          {value: '4', text: 'May'},
          {value: '5', text: 'June'},
          {value: '6', text: 'July'},
          {value: '7', text: 'August'},
          {value: '8', text: 'September'},
          {value: '9', text: 'October'},
          {value: '10', text: 'November'},
          {value: '11', text: 'December'},
        ]
        this.mobilePickerMonthsRef.value = this.month.toString()

        const yearsArr = []
        for (let i = +this.yearStart; i <= +this.yearEnd; i++) {
          yearsArr.push({ value: i.toString(), text: i.toString() })
        }

        this.mobilePickerYearsRef.options = yearsArr
        this.mobilePickerYearsRef.value = this.year.toString()

      }, 100)
    }

  }

  onMobilPickerMonthSelectedHandler = (e) => {
    this.month = +e.detail.value
    this.monthDetails = this.getMonthDetails(this.year, this.month)
  }

  onMobilPickerYearSelectedHandler = (e) => {
    this.year = +e.detail.value
    this.monthDetails = this.getMonthDetails(this.year, this.month)
  }


  render() {
    return (
      <Host>
        <div class="snap-calendar">
          <snap-input
            onSnap-input-focus={this.onInputFocusHandler}
            onSnap-input-blur={this.onInputBlurHandler}
            onSnap-input-date-change={this.onInputDateChangeHandler}
            _id="snap-calendar"
            _type="text"
            placeholder="MM/DD/YYYY"
            ref={ref => (this.inputRef = ref)}
            date={true}
            errorDescription={this.errorDescription && !this.showDatePicker ? this.errorDescription : ''}
          >
          </snap-input>

          { this.showDatePicker && !this.isMobile &&
            <div class="snap-calendar-date-picker" ref={ref => (this.datePickerContainerRef = ref)}>
              <div class="date-picker-header">
                <div class="month-container">
                  <div class="month-arrow-container" onClick={() => this.setMonth(-1)}>
                    <snap-icon icon="chevron-left-solid" color="#2563EB"></snap-icon>
                  </div>
                  <div class='month'>{ this.getMonth(this.month) }</div>
                  <div class="month-arrow-container" onClick={() => this.setMonth(1)}>
                    <snap-icon icon="chevron-right-solid" color="#2563EB"></snap-icon>
                  </div>
                </div>
                <div class="year-container">
                  <snap-dropdown
                    onSnap-dropdown-item-selected={({ detail }) => this.setYear(detail)}
                    button-text="Year"
                    size="base"
                    modal-type="fullscreen"
                    year-selector="true"
                    set-year={this.year}
                  />
                </div>
              </div>

              <div class='date-picker-body'>
                { this.renderCalendar() }
              </div>

              <div class="date-picker-footer">
                <snap-link text="Today" onClick={() => this.setToday()} />
              </div>
            </div>
          }

          { this.showDatePicker && this.isMobile &&
            <snap-action-sheet
              onSnap-action-sheet-close={() => {
                this.showDatePicker = false
                this.isMobileCalendarShow = true
              }}
              header="Select Date">
              <div class="snap-calendar-date-picker mobile" ref={ref => (this.datePickerContainerRef = ref)}>
                <div class="date-picker-header">
                  <div class="year-container" onClick={this.showMobileDatePicker}>
                    { this.getMonth(this.month) } { this.year }
                    <snap-icon icon="chevron-down-solid" color="#64748B" size="sm"></snap-icon>
                  </div>
                  { this.isMobileCalendarShow &&
                    <div class="month-container">
                      <div class="month-arrow-container" onClick={() => this.setMonth(-1)}>
                        <snap-icon icon="chevron-left-solid" color="#2563EB"></snap-icon>
                      </div>
                      <div class="month-arrow-container" onClick={() => this.setMonth(1)}>
                        <snap-icon icon="chevron-right-solid" color="#2563EB"></snap-icon>
                      </div>
                    </div>
                  }
                </div>

                <div class='date-picker-body'>
                  { this.isMobileCalendarShow ?
                    <span>{this.renderCalendar()}</span>
                    :
                    <span class="body-pickers-container">
                      <snap-mobile-picker
                        rightAligned={true}
                        onSnap-mobile-picker-selected={this.onMobilPickerMonthSelectedHandler}
                        ref={ref => (this.mobilePickerMonthsRef = ref)}
                      ></snap-mobile-picker>
                      <snap-mobile-picker
                        onSnap-mobile-picker-selected={this.onMobilPickerYearSelectedHandler}
                        ref={ref => (this.mobilePickerYearsRef = ref)}
                      ></snap-mobile-picker>
                    </span>
                  }
                </div>

                <div class={`date-picker-footer ${!this.isMobileCalendarShow ? 'right' : ''}`}>
                  { this.isMobileCalendarShow ?
                    <div class="date-picker-footer-left">
                      <snap-link text="Today" onClick={() => this.setToday()} />
                      <snap-link text="Enter Manually" onClick={() => this.enterManually()} />
                    </div>
                    :
                    <snap-link text="Back to Calendar" onClick={() => { this.isMobileCalendarShow = true }} />
                  }
                </div>
              </div>
            </snap-action-sheet>
          }
        </div>
      </Host>
    )
  }
}
